import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import {
  getSavedSmartViewsCategory,
  setSmartViewsPanelSizeBasedOnState,
} from '../../../grid/genericGridSmartViews/utils';
import { convertGenericViewConfigToGridViewConfig } from '../../../grid/genericGridSmartViews/types';
import { TemplateState } from '../../../types/common/types';
import {
  WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS,
  WEB_COMPONENT_GRID_VIEW,
  WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT,
} from '../../../webcomponents.constants';
import { GzPushBodyLayoutWebComponent } from '../../../ui/gzPushBodyLayout';
import { GridViewWebComponent } from '../../../grid/gridView';
import { GenericGridSmartViewsWebComponent } from '../../../grid/genericGridSmartViews';
import { GzSideMenuWebComponent } from '../../../ui/gzSideMenu';
import { ElementClass } from '../../../utils/htmlElement.utils';
import {
  changeEmptyStateIcon,
  getCompanyFilterUiConfig,
  getOSTypeColumnValues,
  getStateColumnValues,
  getStatusColumnValues,
  getWatchlistColumnValues,
  processExternalFilters,
  setBulkActions,
  setGridCommonProperties,
  setTranslatedStrings,
} from '../../common/utils';
import { UsersAppGridDataProvider } from '../dataProvider/UsersAppGridDataProvider';
import { translateFn } from '../../../utils/translator.helper';
import { ColumnType } from '../../../grid/types/common';
import { RemoteDetailsTypes } from '../../../filters/backendEnumFilter/types';
import { ColumnIndexes, ColumnKeys, UsersAppWebComponentSchema, UserType } from '../types';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import {
  companyRenderer,
  regionRenderer,
  resourceIdRenderer,
  riskScoreRenderer,
  usernameRenderer,
  osesRenderer,
} from '../../common/customRenderers';

import {
  actionsColumnRenderer,
  departmentRenderer,
  risksRenderer,
  userDevicesRenderer,
  userTypeRenderer,
} from './CustomGridRenderers';
import { RiskTypes } from '../../common/constants';

export class UsersAppView extends BaseWebComponentView {
  public riskType = RiskTypes.USERS;
  public filterDependencyMapping = {
    companyId: ['username', 'devices.items.endpointId', 'department', 'region', 'resourceId'],
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('gz-push-body-layout .wrapper');
    this.dom.$title = this.shadow.querySelector('gz-push-body-layout .title');
    this.dom.$pageTitle = this.shadow.querySelector('.page_title');
    this.dom.$loader = this.shadow.querySelector('gz-push-body-layout .loader');

    this.dom.$pushBodyLayout = this.shadow.querySelector(
      WEB_COMPONENT_GZ_PUSH_BODY_LAYOUT
    ) as GzPushBodyLayoutWebComponent;

    this.dom.$grid = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GRID_VIEW}`
    ) as GridViewWebComponent<ColumnIndexes, ColumnKeys>;

    this.dom.$loadingErrorMessage = this.shadow.querySelector('gz-push-body-layout .loading-error--message');
    this.dom.$gridSmartViews = this.shadow.querySelector(
      `gz-push-body-layout ${WEB_COMPONENT_GENERIC_GRID_SMART_VIEWS}`
    ) as GenericGridSmartViewsWebComponent;
    this.dom.$smartViewsTitle = this.shadow.querySelector('gz-push-body-layout #smartViewsTitle');

    this.dom.$sideMenu = this.shadow.querySelector('gz-push-body-layout #sideMenu') as GzSideMenuWebComponent;
    this.dom.$riskManagementAdditionalText = this.shadow.querySelector('#riskManagementAdditionalText');
    this.dom.$scanEmptyStateButton = this.shadow.querySelector('#scan-button-empty-state');
    this.dom.$scanEmptyStateTooltip = this.shadow.querySelector('#scan-button-empty-state-tooltip');
    this.dom.$overlay = this.shadow.querySelector('#extra-details-context-menu');

    setBulkActions.call(this, {
      ignore: translateFn('Ignore identities'),
      restore: translateFn('Restore ignored identities'),
    });

    this.setTranslatedStrings();
  }
  setTranslatedStrings() {
    setTranslatedStrings.call(this);
    this.dom.$pageTitle.innerHTML = this.dependencies.translateFn('Identities');
  }

  getTranslations() {
    return {
      smartViewsStrings: this.stringsTranslator.getSmartViewsTranslations(),
      defaultViewsTranslations: {
        all_risk_management_users: this.dependencies.translateFn('All identities'),
        high_risk_level_risk_management_users: this.dependencies.translateFn('High risk score'),
        detected_status_risk_management_users: this.dependencies.translateFn('Detected'),
        ignored_state_risk_management_users: this.dependencies.translateFn('Ignored'),
        watchlist_risk_management_users: this.dependencies.translateFn('Watchlist'),
      },
    };
  }

  buildSuccessView(data: UsersAppWebComponentSchema) {
    this.buildSmartViews().then(async () => {
      if (data.externalFilters.length > 0) {
        await this.dom.$gridSmartViews.controller.setDefaultViewAsActiveView();
      }
      const activeView = this.dom.$gridSmartViews.controller.getActiveView();
      setSmartViewsPanelSizeBasedOnState(this.dom.$pushBodyLayout, this.dom.$gridSmartViews.smartViewState);
      await this.buildGrid(convertGenericViewConfigToGridViewConfig(activeView), data.externalFilters);
    });
  }

  buildErrorView() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private async buildSmartViews() {
    if (!(this.dom.$gridSmartViews instanceof GenericGridSmartViewsWebComponent)) {
      throw new Error('Missing grid smart views');
    }
    this.dom.$gridSmartViews.categoriesConfig = getSavedSmartViewsCategory(
      this.dependencies.componentName,
      this.getTranslations().defaultViewsTranslations,
      this.dependencies.api.clients.SmartViews.validateViewName
    );
    this.dom.$gridSmartViews.hasSearchInput = true;
    this.dom.$gridSmartViews.isEditedFn = this.isEdited.bind(this);
    this.dom.$gridSmartViews.injectDependencies(this.dependencies);

    await this.dom.$gridSmartViews.start();
    this.setElementClass(this.dom.$loader, ElementClass.HIDDEN);
    this.removeElementClass(this.dom.$gridSmartViews, ElementClass.HIDDEN);
  }

  async buildGrid(view, externalFilters: Array<any> = []) {
    const {
      hasExternalFilters,
      initialViewWithoutExternalFilters,
      view: processedView,
    } = processExternalFilters(view, externalFilters);

    this.dom.$grid.propertiesSet = false;

    setGridCommonProperties(this.dom.$grid, processedView, hasExternalFilters, initialViewWithoutExternalFilters);
    this.dom.$grid.dataProvider = new UsersAppGridDataProvider(this.dependencies);
    this.dom.$grid.allColumnDetails = this.getColumns();
    this.dom.$grid.filtersUiConfigs = this.getFiltersUiConfig();
    this.dom.$grid.columnRenderers = this.getColumnRenderers();
    this.dom.$grid.predefinedFiltersKeys = this.getPredefinedFiltersKeys();
    this.dom.$grid.sideMenuParent = this.dom.$wrapper;
    this.dom.$grid.filterDependencyMapping = this.filterDependencyMapping;
    this.dom.$grid.propertiesSet = true;

    this.dom.$sideMenu.closeSideMenu();

    if (!this.dom.$grid.started) {
      this.dom.$grid.injectDependencies(this.dependencies);
      await this.dom.$grid.start();
    }

    changeEmptyStateIcon(this.dom.$grid);
    this.removeElementClass(this.dom.$header, ElementClass.HIDDEN);
  }

  isEdited() {
    return false;
  }

  private getColumnRenderers() {
    return {
      [ColumnKeys.COMPANY]: companyRenderer.bind(this),
      [ColumnKeys.RISK_SCORE]: riskScoreRenderer.bind(this),
      [ColumnKeys.RISKS]: risksRenderer.bind(this),
      [ColumnKeys.USER_DEVICES]: userDevicesRenderer.bind(this),
      [ColumnKeys.DEPARTMENT]: departmentRenderer.bind(this),
      [ColumnKeys.USER_TYPE]: userTypeRenderer.bind(this),
      [ColumnKeys.SELECT]: actionsColumnRenderer.bind(this),
      [ColumnKeys.REGION]: regionRenderer.bind(this),
      [ColumnKeys.RESOURCE_ID]: resourceIdRenderer.bind(this),
      [ColumnKeys.USERNAME]: usernameRenderer.bind(this),
      [ColumnKeys.OPERATING_SYSTEMS]: osesRenderer.bind(this),
    };
  }

  private getPredefinedFiltersKeys() {
    const keys: any = [];

    if (!Application.Main.company.isCustomer()) {
      keys.push(ColumnKeys.COMPANY);
    }

    return [...keys, ColumnKeys.USERNAME, ColumnKeys.RISK_SCORE, ColumnKeys.RISKS, ColumnKeys.USER_TYPE];
  }

  getColumns() {
    const allColumns: any = [];

    if (Application.Main.company.isPartner()) {
      allColumns.push({
        column: ColumnIndexes.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.REMOTE_SELECT,
        removable: true,
        sortable: false,
        filterResetValue: {
          exactMatch: Application.Main.user.company.id,
        },
      });
    }

    allColumns.push(
      ...[
        {
          column: ColumnIndexes.USERNAME,
          key: ColumnKeys.USERNAME,
          columnName: this.dependencies.translateFn('Identity name'),
          type: ColumnType.COMPLEX_ENUM_FILTER,
          width: '100px',
          removable: false,
          sortable: false,
          remoteDetails: {
            type: RemoteDetailsTypes.RiskManagementUsername,
          },
        },
        {
          column: ColumnIndexes.RISK_SCORE,
          key: ColumnKeys.RISK_SCORE,
          columnName: this.dependencies.translateFn('Risk score'),
          type: ColumnType.RANGE,
          width: '100px',
          removable: false,
          sortable: true,
        },
        {
          column: ColumnIndexes.RISKS,
          key: ColumnKeys.RISKS,
          columnName: this.dependencies.translateFn('Identity risks'),
          type: ColumnType.COMPLEX_ENUM_FILTER,
          remoteDetails: {
            type: RemoteDetailsTypes.HumanBasedRisksNames,
            ignoredFieldsForFilters: ['label', 'type'],
          },
          removable: false,
        },
        {
          column: ColumnIndexes.USER_TYPE,
          key: ColumnKeys.USER_TYPE,
          columnName: this.dependencies.translateFn('Identity type'),
          type: ColumnType.ENUM,
          values: this.getUserTypeColumnValues(),
          width: '100px',
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.USER_DEVICES,
          key: ColumnKeys.USER_DEVICES,
          columnName: this.dependencies.translateFn('Identity resources'),
          type: ColumnType.COMPLEX_ENUM_FILTER,
          removable: true,
          sortable: false,
          remoteDetails: {
            type: RemoteDetailsTypes.RiskManagementUsersDevices,
          },
        },
        {
          column: ColumnIndexes.DEPARTMENT,
          key: ColumnKeys.DEPARTMENT,
          columnName: this.dependencies.translateFn('Department'),
          type: ColumnType.COMPLEX_ENUM_FILTER,
          removable: true,
          sortable: false,
          remoteDetails: {
            type: RemoteDetailsTypes.RiskManagementUsersDepartments,
          },
        },
        {
          column: ColumnIndexes.RESOURCE_ID,
          key: ColumnKeys.RESOURCE_ID,
          columnName: this.dependencies.translateFn('Resource ID'),
          type: ColumnType.COMPLEX_ENUM_FILTER,
          removable: true,
          sortable: false,
          remoteDetails: {
            type: RemoteDetailsTypes.UsersResourceId,
          },
        },
        {
          column: ColumnIndexes.OPERATING_SYSTEMS,
          key: ColumnKeys.OPERATING_SYSTEMS,
          columnName: this.dependencies.translateFn('Platforms'),
          type: ColumnType.ENUM,
          values: getOSTypeColumnValues(),
          width: '100px',
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.REGION,
          key: ColumnKeys.REGION,
          columnName: this.dependencies.translateFn('Region'),
          type: ColumnType.COMPLEX_ENUM_FILTER,
          removable: true,
          sortable: false,
          remoteDetails: {
            type: RemoteDetailsTypes.UsersRegions,
          },
        },
        {
          column: ColumnIndexes.STATUS,
          key: ColumnKeys.STATUS,
          columnName: this.dependencies.translateFn('Status'),
          type: ColumnType.ENUM,
          values: getStatusColumnValues(),
          width: '100px',
          removable: false,
          sortable: false,
        },
        {
          column: ColumnIndexes.STATE,
          key: ColumnKeys.STATE,
          columnName: this.dependencies.translateFn('State'),
          type: ColumnType.ENUM,
          values: getStateColumnValues(),
          width: '100px',
          removable: false,
          sortable: false,
        },
        {
          column: ColumnIndexes.WATCHLIST,
          key: ColumnKeys.WATCHLIST,
          columnName: this.dependencies.translateFn('In watchlist'),
          type: ColumnType.ENUM,
          values: getWatchlistColumnValues(),
          width: '100px',
          removable: true,
          sortable: false,
        },
        {
          column: ColumnIndexes.SELECT,
          key: ColumnKeys.SELECT,
          columnName: '',
          type: ColumnType.ACTION,
          removable: false,
          sortable: false,
          width: '100px',
          filteringDisabled: true,
        },
      ]
    );

    return allColumns;
  }

  private getFiltersUiConfig() {
    return [
      getCompanyFilterUiConfig(ColumnKeys.COMPANY),
      {
        key: ColumnKeys.RISKS,
        config: {
          filterLabel: this.dependencies.translateFn('Identity risk name'),
        },
      },
    ];
  }

  getUserTypeColumnValues() {
    const values: UserType[] = [
      UserType.LOCAL,
      UserType.AD,
      UserType.IAM_USER,
      UserType.ACTIVE_DIRECTORY_USER,
      UserType.IAM_SERVICE_ACCOUNT,
      UserType.ROOT_USER,
    ];

    return values.map((value: UserType) => {
      return {
        label: BaseWebComponentTranslator.getInstance().getRiskManagementUserType(value),
        value,
      };
    });
  }
}
